import React, { useMemo } from "react";
import TableCard from "./TableCard";

const getGroupTables = (group) => {
  if (Array.isArray(group)) return group.filter(Boolean);
  return group ? [group] : [];
};

const getGroupKey = (group, index) => {
  const tables = getGroupTables(group);
  const first = tables[0];

  return (
    first?.table_id ||
    first?.id ||
    first?.table_no ||
    first?.table_number ||
    first?.table_name ||
    `group-${index}`
  );
};

const TableGrid = ({
  visibleTables,
  handleTableClick,
  handleAddKOT,
  handleUnmerge,
  handleOpenBillingPanel,
  mergeMode,
  selectedForMerge,
  getStatusColor,
}) => {
  const groups = useMemo(
    () => (Array.isArray(visibleTables) ? visibleTables.filter(Boolean) : []),
    [visibleTables]
  );

  const tableCount = useMemo(
    () => groups.reduce((total, group) => total + getGroupTables(group).length, 0),
    [groups]
  );

  const selectedCount = Array.isArray(selectedForMerge) ? selectedForMerge.length : 0;

  return (
    <section className={`ns-table-grid-shell ${mergeMode ? "is-merge-mode" : ""}`}>
      <style>{gridStyles}</style>

      <div className="ns-table-grid-head">
        <div>
          <div className="ns-table-grid-eyebrow">Restaurant Floor</div>
          <h4>Table Grid</h4>
          <p>
            Tap KOT to create an order, Bill to settle running KOTs, or select tables while merge mode is active.
          </p>
        </div>

        <div className="ns-table-grid-meta">
          <span>{groups.length} groups</span>
          <span>{tableCount} tables</span>
          {mergeMode ? <span className="active">{selectedCount} selected</span> : null}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="ns-table-grid-empty">
          <div className="ns-table-grid-empty-icon">⌗</div>
          <strong>No tables found</strong>
          <span>Try clearing the filters or refresh the table list.</span>
        </div>
      ) : (
        <div className="ns-table-grid" role="list" aria-label="Restaurant tables">
          {groups.map((group, groupIndex) => (
            <div
              key={getGroupKey(group, groupIndex)}
              className="ns-table-grid-card-shell"
              role="listitem"
            >
              <TableCard
                group={group}
                groupIndex={groupIndex}
                handleTableClick={handleTableClick}
                handleAddKOT={handleAddKOT}
                handleUnmerge={handleUnmerge}
                handleOpenBillingPanel={handleOpenBillingPanel}
                mergeMode={mergeMode}
                selectedForMerge={selectedForMerge}
                getStatusColor={getStatusColor}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const gridStyles = `
  .ns-table-grid-shell {
    width: 100%;
    min-width: 0;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 22px;
    box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
    overflow: hidden;
  }

  .ns-table-grid-shell.is-merge-mode {
    border-color: rgba(37, 99, 235, 0.28);
    box-shadow: 0 18px 46px rgba(37, 99, 235, 0.12);
  }

  .ns-table-grid-head {
    padding: 13px 15px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background:
      radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
      #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
  }

  .ns-table-grid-eyebrow {
    color: #2563eb;
    font-size: 0.68rem;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 4px;
  }

  .ns-table-grid-head h4 {
    color: #0f172a;
    font-size: 1rem;
    line-height: 1.18;
    font-weight: 950;
    margin: 0;
  }

  .ns-table-grid-head p {
    color: #64748b;
    font-size: 0.76rem;
    line-height: 1.42;
    font-weight: 760;
    margin: 4px 0 0;
    max-width: 720px;
  }

  .ns-table-grid-meta {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    flex-wrap: wrap;
  }

  .ns-table-grid-meta span {
    min-height: 30px;
    border-radius: 999px;
    background: #f8fafc;
    color: #475569;
    border: 1px solid rgba(15, 23, 42, 0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 10px;
    font-size: 0.72rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .ns-table-grid-meta span.active {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
  }

  .ns-table-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(188px, 1fr));
    gap: 10px;
    align-items: stretch;
    padding: 12px 12px 82px;
    background: #f8fafc;
  }

  .ns-table-grid-card-shell {
    min-width: 0;
    min-height: 198px;
    height: 100%;
    display: flex;
  }

  .ns-table-grid-card-shell > * {
    width: 100%;
    height: 100%;
  }

  /*
    These safe child selectors help existing TableCard layouts align better
    without changing the TableCard component itself.
  */
  .ns-table-grid-card-shell .ns-table-card,
  .ns-table-grid-card-shell .table-card,
  .ns-table-grid-card-shell .table-box {
    height: 100%;
    min-height: 198px;
  }

  .ns-table-grid-card-shell button {
    touch-action: manipulation;
  }

  .ns-table-grid-empty {
    min-height: 320px;
    background: #f8fafc;
    color: #64748b;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 32px;
  }

  .ns-table-grid-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 22px;
    background: #eff6ff;
    color: #1d4ed8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    font-weight: 950;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  }

  .ns-table-grid-empty strong {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 950;
  }

  .ns-table-grid-empty span {
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 760;
  }

  @media (min-width: 1500px) {
    .ns-table-grid {
      grid-template-columns: repeat(auto-fill, minmax(196px, 1fr));
    }
  }

  @media (max-width: 1100px) {
    .ns-table-grid {
      grid-template-columns: repeat(auto-fill, minmax(174px, 1fr));
      gap: 10px;
      padding-bottom: 64px;
    }

    .ns-table-grid-head {
      align-items: stretch;
    }

    .ns-table-grid-meta {
      justify-content: flex-start;
    }
  }

  @media (max-width: 560px) {
    .ns-table-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default TableGrid;